import { Card, Empty, Pagination, Segmented, Space, Spin, Typography } from 'antd';
import { API } from 'service/api-route';
import { colors } from 'theme/tokens';
import { useFeed } from './shared/context';
import {
  AuthorCell,
  MediaStrip,
  NeedsReviewPill,
  Pill,
  StatusPill,
  WhyFlagged,
} from './shared/components';
import { FeedAdminCommentRow, FeedAdminPostRow } from './shared/types';
import { fmtDateTime, useFeedList, useFeedSettingsOnce, useUrlFilters } from './shared/utils';
import { ContentActionButtons, ContentActionKey, contentActions, useContentActions } from './moderate-modal';
import { PostBadges } from './posts-tab';
import styles from './styles.module.scss';

const PAGE_SIZE = 20;

type Kind = 'posts' | 'comments' | 'pending' | 'ai';

const POST_KEYS: Record<Exclude<Kind, 'comments'>, ContentActionKey[]> = {
  posts: ['clear', 'hide', 'delete'],
  pending: ['approve', 'reject', 'hide', 'delete'],
  ai: ['unhide', 'delete'],
};

const PostCard = ({ row, kind }: { row: FeedAdminPostRow; kind: Exclude<Kind, 'comments'> }) => {
  const { openPost, myRank, access } = useFeed();
  const { run, modals } = useContentActions();
  const items = contentActions('post', row, { myRank, canMarkImportant: access.canMarkImportant })
    .filter((item) => POST_KEYS[kind].includes(item.key))
    .map((item) =>
      kind === 'ai' && item.key === 'unhide' ? { ...item, label: 'Hiện lại (máy nhầm)', primary: true } : item
    );
  const statusPill =
    kind === 'pending' ? (
      <Pill color={colors.warningInk} bg={colors.warningSoft}>
        Đang giữ — chỉ tác giả thấy
      </Pill>
    ) : kind === 'ai' ? (
      <Pill color={colors.dangerInk} bg={colors.dangerSoft}>
        Máy đã ẩn
      </Pill>
    ) : (
      <StatusPill status={row.status} deletedAt={row.deletedAt} />
    );
  return (
    <Card size="small">
      <div className={styles.toolbar} style={{ marginBottom: 8 }}>
        <div style={{ minWidth: 0, maxWidth: 360 }}>
          <AuthorCell author={row.author} actedBy={row.actedBy} />
        </div>
        <div className={styles.cardMeta}>
          <span>{fmtDateTime(row.createdAt)}</span>
          <span>#{row.id}</span>
          {statusPill}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <MediaStrip thumbs={row.thumbs} size={56} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <Typography.Paragraph ellipsis={{ rows: 3 }} style={{ marginBottom: 2 }}>
            {row.contentPreview || <span style={{ color: colors.textMuted }}>(không có chữ)</span>}
          </Typography.Paragraph>
          <Space size={12} wrap>
            <Typography.Link onClick={() => openPost(row.id)}>Xem đầy đủ</Typography.Link>
            <PostBadges row={row} />
          </Space>
        </div>
      </div>
      {(row.keywordHits?.length > 0 || row.textVerdict === 'REVIEW' || row.textVerdict === 'BLOCK' ||
        (row.mediaVerdicts || []).some((v) => v === 'REVIEW' || v === 'BLOCK')) && (
        <div className={styles.why}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Vì sao cần xem</div>
          <WhyFlagged keywordHits={row.keywordHits} textVerdict={row.textVerdict} mediaVerdicts={row.mediaVerdicts} />
        </div>
      )}
      <div style={{ marginTop: 10 }}>
        <ContentActionButtons
          size="small"
          items={items}
          onRun={(key) => run('post', row.id, key, kind === 'ai' && key === 'unhide' ? `Hiện lại bài #${row.id} (máy nhầm)` : undefined)}
        />
      </div>
      {modals}
    </Card>
  );
};

const CommentCard = ({ row }: { row: FeedAdminCommentRow }) => {
  const { openPost, myRank } = useFeed();
  const { run, modals } = useContentActions();
  const items = contentActions('comment', row, { myRank, canMarkImportant: false }).filter((item) =>
    ['clear', 'approve', 'reject', 'hide', 'delete'].includes(item.key)
  );
  return (
    <Card size="small">
      <div className={styles.toolbar} style={{ marginBottom: 8 }}>
        <div style={{ minWidth: 0, maxWidth: 360 }}>
          <AuthorCell author={row.author} />
        </div>
        <div className={styles.cardMeta}>
          <span>{fmtDateTime(row.createdAt)}</span>
          <span>Bình luận #{row.id}</span>
          {row.status === 'PENDING_REVIEW' ? (
            <Pill color={colors.warningInk} bg={colors.warningSoft}>
              Đang giữ — chỉ tác giả thấy
            </Pill>
          ) : (
            <StatusPill status={row.status} deletedAt={row.deletedAt} />
          )}
          {row.needsReview && <NeedsReviewPill />}
        </div>
      </div>
      {row.replyTo && (
        <div style={{ fontSize: 12, color: colors.textSecondary }}>↳ trả lời @{row.replyTo.name}</div>
      )}
      <div className={styles.content} style={{ fontSize: 14 }}>
        {row.content}
      </div>
      {(row.keywordHits?.length > 0 || row.textVerdict === 'REVIEW' || row.textVerdict === 'BLOCK') && (
        <div className={styles.why}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Vì sao cần xem</div>
          <WhyFlagged keywordHits={row.keywordHits} textVerdict={row.textVerdict} />
        </div>
      )}
      {row.post && (
        <div style={{ fontSize: 13, marginTop: 6 }}>
          Trong bài:{' '}
          <Typography.Link onClick={() => openPost(row.post.id, 'comments')}>
            {row.post.contentPreview || `#${row.post.id}`}
          </Typography.Link>
        </div>
      )}
      <div style={{ marginTop: 10 }}>
        <ContentActionButtons size="small" items={items} onRun={(key) => run('comment', row.id, key)} />
      </div>
      {modals}
    </Card>
  );
};

const PostList = ({ kind }: { kind: Exclude<Kind, 'comments'> }) => {
  const { refreshKey } = useFeed();
  const { get, set } = useUrlFilters();
  const page = Number(get('page')) || 1;
  const query =
    kind === 'posts'
      ? { status: 'PUBLISHED', flagged: true }
      : kind === 'pending'
      ? { status: 'PENDING_REVIEW' }
      : { status: 'HIDDEN', hiddenReason: 'AI_BLOCK' };
  const { items, total, loading } = useFeedList<FeedAdminPostRow>(
    API.feedAdminPosts,
    { ...query, page, limit: PAGE_SIZE },
    { refreshKey }
  );
  const empty =
    kind === 'posts'
      ? 'Không có bài nào cần xem.'
      : kind === 'pending'
      ? 'Không có bài nào đang bị giữ.'
      : 'Máy chưa ẩn bài nào.';
  if (loading && !items.length) return <Spin />;
  if (!items.length) return <Empty description={empty} />;
  return (
    <div className={styles.cards}>
      {items.map((row) => (
        <PostCard key={row.id} row={row} kind={kind} />
      ))}
      {total > PAGE_SIZE && (
        <Pagination
          current={page}
          pageSize={PAGE_SIZE}
          total={total}
          showSizeChanger={false}
          onChange={(p) => set({ page: p })}
        />
      )}
    </div>
  );
};

const CommentList = () => {
  const { refreshKey } = useFeed();
  const { get, set } = useUrlFilters();
  const page = Number(get('page')) || 1;
  // Bình luận đang giữ (chỉ có khi bật "Giữ bài chờ duyệt") hiện trước, rồi bình luận có cờ.
  const pending = useFeedList<FeedAdminCommentRow>(
    API.feedAdminComments,
    { status: 'PENDING_REVIEW', page: 1, limit: 50 },
    { refreshKey }
  );
  const flagged = useFeedList<FeedAdminCommentRow>(
    API.feedAdminComments,
    { flagged: true, page, limit: PAGE_SIZE },
    { refreshKey }
  );
  const seen = new Set<number>();
  const rows = (page === 1 ? pending.items : [])
    .concat(flagged.items.filter((c) => c.status === 'PUBLISHED' || c.status === 'PENDING_REVIEW'))
    .filter((c) => {
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
  if ((pending.loading || flagged.loading) && !rows.length) return <Spin />;
  if (!rows.length) return <Empty description="Không có bình luận nào cần xem." />;
  return (
    <div className={styles.cards}>
      {rows.map((row) => (
        <CommentCard key={row.id} row={row} />
      ))}
      {flagged.total > PAGE_SIZE && (
        <Pagination
          current={page}
          pageSize={PAGE_SIZE}
          total={flagged.total}
          showSizeChanger={false}
          onChange={(p) => set({ page: p })}
        />
      )}
    </div>
  );
};

/** Tab "Cần xem" (03 §9): bài đăng thẳng có cờ, bình luận, bài đang giữ, bài máy đã ẩn. */
const ReviewTab = () => {
  const { summary } = useFeed();
  const settings = useFeedSettingsOnce();
  const { get, clear } = useUrlFilters();
  const kind = (get('kind') as Kind) || 'posts';
  const showPending = !!settings?.holdOnReview || (summary?.pendingPosts ?? 0) > 0 || kind === 'pending';
  const commentCount = (summary?.flaggedComments ?? 0) + (summary?.pendingComments ?? 0);

  const options = [
    { value: 'posts', label: `Bài đã đăng${summary ? ` (${summary.flaggedPosts})` : ''}` },
    { value: 'comments', label: `Bình luận${summary ? ` (${commentCount})` : ''}` },
    ...(showPending
      ? [{ value: 'pending', label: `Đang giữ${summary ? ` (${summary.pendingPosts})` : ''}` }]
      : []),
    { value: 'ai', label: 'Máy đã ẩn' },
  ];

  return (
    <div>
      <div style={{ marginBottom: 12, overflowX: 'auto' }}>
        <Segmented
          value={kind}
          options={options}
          onChange={(v) => clear(['tab', 'post'], v !== 'posts' ? { kind: String(v) } : undefined)}
        />
      </div>
      <div className={styles.hint} style={{ marginBottom: 12 }}>
        {kind === 'posts'
          ? 'Bài dính từ "cần xem" hoặc AI "cần xem" vẫn được đăng (quyết định "đăng thẳng") — xem lại và ẩn nếu vi phạm.'
          : kind === 'pending'
          ? 'Bài đang bị giữ vì "Giữ bài chờ duyệt" đang bật: chỉ tác giả thấy tới khi quản lý duyệt.'
          : kind === 'ai'
          ? 'Bài bị máy soi AI ẩn — kiểm tra để bắt các lần máy chặn nhầm.'
          : 'Bình luận đang giữ chờ duyệt và bình luận có cờ "cần xem".'}
      </div>
      {kind === 'comments' ? <CommentList /> : <PostList key={kind} kind={kind} />}
    </div>
  );
};

export default ReviewTab;
